<template>
  <div class="upload-container">
    <el-upload class="upload-demo" drag multiple accept="image/*" :auto-upload="false" :show-file-list="false"
      :on-change="handleFileChange" :on-remove="handleFileRemove" :before-upload="beforeUpload" :disabled="props.isTranslating">
      <div class="el-upload__icon">
        <i class="fas fa-images"></i>
      </div>
      <div class="el-upload__text">{{ props.isTranslating ? $t('manga.upload.translating') : $t('manga.upload.dragHint') }}</div>
      <div class="el-upload__tip">
        {{ $t('manga.upload.tip', { max: maxCount }) }}
      </div>
    </el-upload>

    <div class="progress-section" v-if="uploading">
      <el-progress :percentage="progress" status="success" :stroke-width="8"></el-progress>
      <div class="loading-text">
        <el-icon class="is-loading">
          <Loading />
        </el-icon>
        {{ props.isTranslating ? $t('manga.upload.translating') : $t('manga.upload.processing') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'

const emit = defineEmits(['file-change'])
const { t } = useI18n()

const selectedFiles = ref([])
const uploadedImages = ref([])
const currentImageIndex = ref(0)
const uploading = ref(false)
const progress = ref(0)

const props = defineProps({
  isTranslating: {
    type: Boolean,
    default: false
  },
  isVip: {
    type: Boolean,
    default: false
  }
})

const maxCount = computed(() => props.isVip ? 50 : 20)

const handleFileChange = (file) => {
  ElMessage.closeAll()

  // 如果正在翻译，禁止添加新文件
  if (props.isTranslating) {
    ElMessage.warning(t('manga.upload.waitTranslate'))
    return
  }

  const isImage = file.raw.type.startsWith('image/')
  const isLt10M = file.raw.size / 1024 / 1024 < 10

  if (!isImage) {
    ElMessage.error(t('manga.upload.onlyImages'))
    return
  }
  if (!isLt10M) {
    ElMessage.error(t('manga.upload.sizeLimit'))
    return
  }

  // 检查是否已存在相同文件（使用更精确的比较）
  const exists = selectedFiles.value.some(f =>
    f.name === file.name &&
    f.size === file.size &&
    f.lastModified === file.lastModified
  )
  if (exists) {
    ElMessage.warning(t('manga.upload.fileExists', { name: file.name }))
    return
  }

  // 限制最多N张图片
  if (selectedFiles.value.length >= maxCount.value) {
    ElMessage.warning(t('manga.upload.maxFiles', { max: maxCount.value }))
    return
  }

  // 检查总大小不超过50MB
  const totalSize = selectedFiles.value.reduce((sum, f) => sum + f.raw.size, 0) + file.raw.size
  if (totalSize > 50 * 1024 * 1024) {
    ElMessage.warning(t('manga.upload.totalSizeLimit'))
    return
  }

  // 添加新文件
  selectedFiles.value.push(file)
  const imageUrl = URL.createObjectURL(file.raw)
  uploadedImages.value.push(imageUrl)

  // 如果是第一个文件，设置为当前显示
  if (selectedFiles.value.length === 1) {
    currentImageIndex.value = 0
  }

  // 更新父组件
  emit('file-change', {
    file: file.raw,
    imageUrl: imageUrl,
    allFiles: selectedFiles.value.map(f => f.raw || f),
    allImages: uploadedImages.value
  })

  ElMessage.success(t('manga.upload.added', { name: file.name, count: selectedFiles.value.length }))
}

const beforeUpload = () => {
  return false
}

const clearAllFiles = () => {
  // 释放所有图片URL的内存
  uploadedImages.value.forEach(url => URL.revokeObjectURL(url))
  selectedFiles.value = []
  uploadedImages.value = []
  currentImageIndex.value = 0
  emit('file-change', {
    file: null,
    imageUrl: '',
    allFiles: [],
    allImages: [],
    clearAll: true  // 添加标记，表示是清空操作
  })
}

const handleFileRemove = (file) => {
  const index = selectedFiles.value.findIndex(f =>
    f.name === file.name &&
    f.size === file.size &&
    f.lastModified === file.lastModified
  )
  if (index !== -1) {
    // 释放内存
    if (uploadedImages.value[index]) {
      URL.revokeObjectURL(uploadedImages.value[index])
    }

    // 移除文件
    selectedFiles.value.splice(index, 1)
    uploadedImages.value.splice(index, 1)

    // 调整当前索引
    if (currentImageIndex.value >= selectedFiles.value.length) {
      currentImageIndex.value = Math.max(0, selectedFiles.value.length - 1)
    }

    // 更新父组件
    if (selectedFiles.value.length > 0) {
      emit('file-change', {
        file: selectedFiles.value[currentImageIndex.value]?.raw || selectedFiles.value[currentImageIndex.value],
        imageUrl: uploadedImages.value[currentImageIndex.value],
        allFiles: selectedFiles.value.map(f => f.raw || f),
        allImages: uploadedImages.value
      })
    } else {
      emit('file-change', { file: null, imageUrl: '', allFiles: [], allImages: [] })
    }

    ElMessage.success(t('manga.upload.removed', { name: file.name, count: selectedFiles.value.length }))
  }
}

// 这个组件现在负责上传区域的UI展示和文件选择

// 暴露给父组件的方法
defineExpose({
  clearAllFiles
})
</script>

<style scoped>
.upload-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.upload-demo {
  width: 100%;
  height: 100%;
}

:deep(.el-upload) {
  width: 100%;
  height: 100%;
}

:deep(.el-upload-dragger) {
  background: rgba(30, 41, 59, 0.3) !important;
  border: 2px dashed rgba(94, 234, 212, 0.3) !important;
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 2rem 1rem !important;
  width: 100%;
}

:deep(.el-upload-dragger:hover) {
  border-color: #5eead4 !important;
  background: rgba(94, 234, 212, 0.05) !important;
}

:deep(.el-upload-dragger.is-dragover) {
  border-color: #5eead4 !important;
  background: rgba(94, 234, 212, 0.1) !important;
}

.el-upload__icon {
  font-size: 3rem;
  color: #5eead4;
  margin-bottom: 1rem;
}

:deep(.el-upload__text) {
  color: #cbd5e1 !important;
  font-size: 1.1rem !important;
  margin-bottom: 0.5rem !important;
}

:deep(.el-upload__text em) {
  color: #5eead4 !important;
  font-style: normal !important;
}

:deep(.el-upload__tip) {
  color: #94a3b8 !important;
  font-size: 0.9rem !important;
  margin-top: 0.5rem !important;
}

.progress-section {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(30, 41, 59, 0.3);
  border-radius: 8px;
}

.loading-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 0.5rem;
  color: #5eead4;
  font-size: 0.9rem;
}

:deep(.el-progress__text) {
  color: #5eead4 !important;
}

:deep(.el-progress-bar__inner) {
  background: linear-gradient(90deg, #5eead4, #3b82f6) !important;
}

:deep(.el-progress-bar__outer) {
  background: rgba(94, 234, 212, 0.1) !important;
}

/* 响应式调整 */
@media (max-width: 768px) {
  :deep(.el-upload-dragger) {
    padding: 1.5rem 0.5rem !important;
  }

  .el-upload__icon {
    font-size: 2.5rem;
  }

  :deep(.el-upload__text) {
    font-size: 1rem !important;
  }
}
</style>